import _ from 'lodash';
import { useEffect, useState } from 'react';
import { fetchHolterEventsDailyCount } from '@/entities/event';
import { KEY_CANCEL, KEY_RECOIL, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import { cancelAllAPI, logError } from '@/shared/lib/apiHelpers';
import {
  selectDailyArtifactCount,
  selectJumpDate,
  selectSelectedDateValue,
  useReportStore,
} from '@/entities/report';
import type { ArtifactAreasThumbnailProps } from './artifactAreasThumbnail';

/**
 * Drives the per-day artifact thumbnail rail of the Beats and HR tab, exposing the daily artifact counts, the selected date and the thumbnail click handler. On mount it loads the artifact daily counts for the study's report window and seeds the selected date and total strip count, wires TAB_1 report-store setters for date, paging, strips, columns and ECG object, and on a jump-date request re-selects the matching day's thumbnail.
 *
 * @param props - Study, profile, report window and timezone offset for the artifact thumbnails.
 * @returns An object carrying isLoadingThumbnail, dailyCount, selectedDateValue and onClickThumbnail.
 */
const useArtifactAreasThumbnail = ({
  timezoneOffset,
  profileId,
  studyId,
  startReport,
  stopReport,
}: ArtifactAreasThumbnailProps) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;

  const selectedDateValue = useReportStore(selectSelectedDateValue(keyRecoil));
  const setSelectedDateValueAction = useReportStore((s) => s.actions.setSelectedDateValue);
  /**
   * Sets which report day the artifact tab is showing. Passes the TAB_1 key with the new selected date or updater to the store's setSelectedDateValue action.
   *
   * @param v - The next selected date, or an updater receiving the previous one.
   * @returns Whatever the store's setSelectedDateValue action returns.
   */
  const setSelectedDateValue = (v: Parameters<typeof setSelectedDateValueAction>[1]) =>
    setSelectedDateValueAction(keyRecoil, v);
  const jumpDate = useReportStore(selectJumpDate(keyRecoil));
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Sets the date the artifact ECG viewer should jump to. Passes the TAB_1 key with the new jump date or updater to the store's setJumpDate action.
   *
   * @param v - The next jump date, or an updater receiving the previous one.
   * @returns Whatever the store's setJumpDate action returns.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const dailyCount = useReportStore(selectDailyArtifactCount(keyRecoil));
  const setDailyCount = useReportStore((s) => s.actions.setDailyArtifactCount);
  const setIsSkipSelectThumbnail = useReportStore((s) => s.actions.setIsSkipSelectThumbnail);
  const setTotalStrip = useReportStore((s) => s.actions.setTotalStrip);
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Stores the artifact strip currently selected for preview. Passes the TAB_1 key with the new selected strip or updater to the store's setSelectedStrip action.
   *
   * @param v - The next selected strip, or an updater receiving the previous one.
   * @returns Whatever the store's setSelectedStrip action returns.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Sets the current page of the artifact strip pagination. Passes the TAB_1 key with the new page index or updater to the store's setPageIndex action.
   *
   * @param v - The next page index, or an updater receiving the previous one.
   * @returns Whatever the store's setPageIndex action returns.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const setIsEmptyDataAction = useReportStore((s) => s.actions.setIsEmptyData);
  /**
   * Flags whether the artifact tab currently has no data to display. Passes the TAB_1 key with the new empty-data flag or updater to the store's setIsEmptyData action.
   *
   * @param v - The next empty-data flag, or an updater receiving the previous one.
   * @returns Whatever the store's setIsEmptyData action returns.
   */
  const setIsEmptyData = (v: Parameters<typeof setIsEmptyDataAction>[1]) =>
    setIsEmptyDataAction(keyRecoil, v);
  const setHolterEventsAction = useReportStore((s) => s.actions.setHolterEvents);
  /**
   * Stores the holter event list backing the artifact tab. Passes the TAB_1 key with the new holter event array or updater to the store's setHolterEvents action.
   *
   * @param v - The next holter event array, or an updater receiving the previous one.
   * @returns Whatever the store's setHolterEvents action returns.
   */
  const setHolterEvents = (v: Parameters<typeof setHolterEventsAction>[1]) =>
    setHolterEventsAction(keyRecoil, v);
  const setEcgObjectAction = useReportStore((s) => s.actions.setEcgObject);
  /**
   * Stores the decoded ECG object rendered by the artifact tab. Passes the TAB_1 key with the new ECG object or updater to the store's setEcgObject action.
   *
   * @param v - The next ECG object, or an updater receiving the previous one.
   * @returns Whatever the store's setEcgObject action returns.
   */
  const setEcgObject = (v: Parameters<typeof setEcgObjectAction>[1]) =>
    setEcgObjectAction(keyRecoil, v);
  const setJumpTimeAction = useReportStore((s) => s.actions.setJumpTime);
  /**
   * Sets the time of day the artifact ECG viewer should jump to. Passes the TAB_1 key with the new jump time or updater to the store's setJumpTime action.
   *
   * @param v - The next jump time, or an updater receiving the previous one.
   * @returns Whatever the store's setJumpTime action returns.
   */
  const setJumpTime = (v: Parameters<typeof setJumpTimeAction>[1]) =>
    setJumpTimeAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Sets which portion of the jumped-to day the artifact viewer scrolls into. Passes the TAB_1 key with the new jump portion or updater to the store's setJumpPortion action.
   *
   * @param v - The next jump portion, or an updater receiving the previous one.
   * @returns Whatever the store's setJumpPortion action returns.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const resetControlModalAction = useReportStore((s) => s.actions.resetControlModal);
  /**
   * Clears the artifact tab's control modal state back to its defaults. Forwards the TAB_1 key to the store's resetControlModal action.
   *
   * @returns Whatever the store's resetControlModal action returns.
   */
  const resetControlModal = () => resetControlModalAction(keyRecoil);
  const setSelectedColumnInfoAction = useReportStore((s) => s.actions.setSelectedColumnInfo);
  /**
   * Stores the event column currently selected in the artifact grid. Passes the TAB_1 key with the new column info or updater to the store's setSelectedColumnInfo action.
   *
   * @param v - The next column info, or an updater receiving the previous one.
   * @returns Whatever the store's setSelectedColumnInfo action returns.
   */
  const setSelectedColumnInfo = (v: Parameters<typeof setSelectedColumnInfoAction>[1]) =>
    setSelectedColumnInfoAction(keyRecoil, v);
  const setSelectedEventInfoAction = useReportStore((s) => s.actions.setSelectedEventInfo);
  /**
   * Stores the events selected inside the current artifact column. Passes the TAB_1 key with the new event info or updater to the store's setSelectedEventInfo action.
   *
   * @param v - The next event info, or an updater receiving the previous one.
   * @returns Whatever the store's setSelectedEventInfo action returns.
   */
  const setSelectedEventInfo = (v: Parameters<typeof setSelectedEventInfoAction>[1]) =>
    setSelectedEventInfoAction(keyRecoil, v);
  const setPrevColumnInfoAction = useReportStore((s) => s.actions.setPrevColumnInfo);
  /**
   * Stores the event column preceding the current selection in the artifact grid. Passes the TAB_1 key with the new column info or updater to the store's setPrevColumnInfo action.
   *
   * @param v - The next column info, or an updater receiving the previous one.
   * @returns Whatever the store's setPrevColumnInfo action returns.
   */
  const setPrevColumnInfo = (v: Parameters<typeof setPrevColumnInfoAction>[1]) =>
    setPrevColumnInfoAction(keyRecoil, v);
  const setNextColumnInfoAction = useReportStore((s) => s.actions.setNextColumnInfo);
  /**
   * Stores the event column following the current selection in the artifact grid. Passes the TAB_1 key with the new column info or updater to the store's setNextColumnInfo action.
   *
   * @param v - The next column info, or an updater receiving the previous one.
   * @returns Whatever the store's setNextColumnInfo action returns.
   */
  const setNextColumnInfo = (v: Parameters<typeof setNextColumnInfoAction>[1]) =>
    setNextColumnInfoAction(keyRecoil, v);
  const setSelectedMultipleStripAction = useReportStore((s) => s.actions.setSelectedMultipleStrip);
  /**
   * Stores the multi-selected artifact strips. Passes the TAB_1 key with the new strip array or updater to the store's setSelectedMultipleStrip action.
   *
   * @param v - The next strip array, or an updater receiving the previous one.
   * @returns Whatever the store's setSelectedMultipleStrip action returns.
   */
  const setSelectedMultipleStrip = (v: Parameters<typeof setSelectedMultipleStripAction>[1]) =>
    setSelectedMultipleStripAction(keyRecoil, v);

  const [isLoadingThumbnail, setIsLoadingThumbnail] = useState(false);

  /**
   * Loads the per-day count of artifact rhythm events for the study's report window and seeds the thumbnail selection. Raises the loading flag, calls fetchHolterEventsDailyCount under the holter AI cancel key, keeps the already selected date when it still exists otherwise falls back to the first day, stores the matching total strip count, and on failure logs and shows an error toast.
   *
   * @returns A promise resolving once the daily counts are stored and the loading flag is cleared.
   */
  const fetchDailyCount = async () => {
    setIsLoadingThumbnail(true);
    try {
      const filter = {
        studyId,
        profileId,
        types: [TYPE_RHYTHM_EVENT_ENUM.ARTIFACT],
        startSearchDate: startReport,
        stopSearchDate: stopReport,
      };
      const artifactDailyCount = await fetchHolterEventsDailyCount(
        filter,
        KEY_CANCEL.API_HOLTER_AI,
      );
      if (selectedDateValue) {
        const foundDate = _.find(artifactDailyCount, (x) => x.date === selectedDateValue);
        setSelectedDateValue(foundDate?.date);
        setTotalStrip(foundDate?.count ?? 0);
      } else {
        setSelectedDateValue(artifactDailyCount[0]?.date);
        setTotalStrip(artifactDailyCount[0]?.count ?? 0);
      }
      setDailyCount(keyRecoil, artifactDailyCount);
    } catch (error) {
      logError('Failed to fetch artifact event count: ', error);
      toastrError(error.message, 'Error');
    }
    setIsLoadingThumbnail(false);
  };

  /**
   * Switches the artifact view to another day when a thumbnail other than the current one is clicked. Cancels in-flight API calls, resets the control modal, rewinds paging to the first page, clears selected, previous and next column info, selected strips, holter events and the ECG object, applies the new date and its strip count, and clears any pending jump date, time and portion.
   *
   * @param thumbnailItem - The clicked day entry, carrying its date and event count.
   * @returns Nothing.
   */
  const onClickThumbnail = (thumbnailItem: any) => {
    if (thumbnailItem.date !== selectedDateValue) {
      cancelAllAPI();
      // resetEcgViewerConfigStore(););
      resetControlModal();
      setPageIndex((prev) => (prev.index === 0 ? prev : { index: 0 }));
      setSelectedDateValue(thumbnailItem.date);
      setSelectedStrip(null);
      setSelectedMultipleStrip([]);
      setTotalStrip(thumbnailItem.count ?? 0);
      setIsEmptyData(false);
      setSelectedColumnInfo([]);
      setSelectedEventInfo([]);
      setPrevColumnInfo([]);
      setNextColumnInfo([]);
      setHolterEvents([]);
      setEcgObject({});
      setJumpDate(undefined);
      setJumpTime(undefined);
      setJumpPortion(0);
      setIsSkipSelectThumbnail(keyRecoil, false);
    }
  };

  useEffect(() => {
    fetchDailyCount();
  }, []);

  useUpdateEffect(() => {
    if (jumpDate && !_.isEmpty(dailyCount)) {
      const dateString = convertToMomentJs(jumpDate, timezoneOffset).format('MM/DD');
      const foundDate = dailyCount.find(
        (x) => convertToMomentJs(x.date, timezoneOffset).format('MM/DD') === dateString,
      );
      if (foundDate) {
        setIsSkipSelectThumbnail(keyRecoil, true);
        setSelectedDateValue(foundDate.date);
        setTotalStrip(foundDate.count ?? 0);
      }
    }
  }, [dailyCount, jumpDate]);

  return {
    isLoadingThumbnail,
    dailyCount,
    selectedDateValue,
    onClickThumbnail,
  };
};

export default useArtifactAreasThumbnail;
